import api from './api';
import { toQueryDateYMD } from '../utils/formatters';

export interface StockInmovilizadoParams {
    fechaDesde: string;
    fechaHasta: string;
    codigoArticulo?: string;
    nroUbicacion?: number;
    page?: number;
    pageSize?: number;
}

export interface StockInmovilizadoRow {
    codigoArticulo: string;
    descripcion: string;
    unidadMedida: string;
    cantidad: number;
    costoNeto: number;
    valorizado: number;
    ultimaVenta: string | null;
}

export interface StockInmovilizadoResponse {
    data: StockInmovilizadoRow[];
    totalCount: number;
    /** Suma de cantidades tal cual (puede mezclar U.M.; el FE prioriza totalValorizado). */
    totalCantidad: number;
    totalValorizado: number;
}

export function formatStockInmovilizadoNumero(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('es-AR', { maximumFractionDigits: 10 });
}

export function buildStockInmovilizadoQuery(params: StockInmovilizadoParams): string {
    const queryParams = new URLSearchParams();
    queryParams.append('fechaDesde', params.fechaDesde);
    queryParams.append('fechaHasta', params.fechaHasta);
    if (params.codigoArticulo) queryParams.append('codigoArticulo', params.codigoArticulo);
    if (params.nroUbicacion) queryParams.append('nroUbicacion', params.nroUbicacion.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    return queryParams.toString();
}

export interface StockAFechaParams {
    fechaHasta: string;
    codigoArticulo?: string;
    nroUbicacion?: number;
    page?: number;
    pageSize?: number;
}

export interface StockAFechaRow {
    codigoArticulo: string;
    descripcion: string;
    unidadMedida: string;
    cantidad: number;
    costoNeto: number;
    valorizado: number;
}

export interface StockAFechaResponse {
    data: StockAFechaRow[];
    totalCount: number;
    totalCantidad: number;
    totalValorizado: number;
}

export function formatStockAFechaNumero(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('es-AR', { maximumFractionDigits: 10 });
}

function buildStockAFechaQuery(params: StockAFechaParams): string {
    const queryParams = new URLSearchParams();
    queryParams.append('fechaHasta', params.fechaHasta);
    if (params.codigoArticulo) queryParams.append('codigoArticulo', params.codigoArticulo);
    if (params.nroUbicacion) queryParams.append('nroUbicacion', params.nroUbicacion.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    return queryParams.toString();
}

export interface StockDepositoRow {
    codigoArticulo: string;
    descripcion: string;
    tipoArticulo?: string | null;
    /** UM base del artículo (Stock.IdUnidadMedida). */
    unidadMedida?: string | null;
    nroUbicacion: number;
    nombreUbicacion: string;
    cantidadDeposito: number;
    cantidadAsignada: number;
    disponibleDeposito: number;
}

export interface StockDepositoParams {
    codigoArticulo?: string;
    descripcion?: string;
    nroUbicacion?: number;
    codigoLinea?: string;
    page?: number;
    pageSize?: number;
    /** Match exacto por código (usa índice); default false = búsqueda parcial LIKE. */
    exactCodigo?: boolean;
}

export interface StockDepositoResponse {
    data: StockDepositoRow[];
    totalCount: number;
    totalCantidad: number;
    totalAsignado: number;
    totalDisponible: number;
    currentPage?: number;
    pageSize?: number;
    totalPages?: number;
}

export function formatStockDepositoNumero(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('es-AR', { maximumFractionDigits: 2 });
}

/** Cantidad con UM base al lado (p. ej. "12,50 KG"). */
export function formatStockDepositoCantidad(
    cantidad: number,
    unidadMedida?: string | null,
): string {
    const numero = formatStockDepositoNumero(cantidad);
    const um = String(unidadMedida ?? '').trim();
    return um ? `${numero} ${um}` : numero;
}

function buildStockDepositoQuery(params: StockDepositoParams): string {
    const queryParams = new URLSearchParams();
    if (params.codigoArticulo) queryParams.append('codigoArticulo', params.codigoArticulo);
    if (params.descripcion) queryParams.append('descripcion', params.descripcion);
    if (params.nroUbicacion) queryParams.append('nroUbicacion', params.nroUbicacion.toString());
    if (params.codigoLinea) queryParams.append('codigoLinea', params.codigoLinea);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.exactCodigo) queryParams.append('exactCodigo', 'true');
    return queryParams.toString();
}

export interface MovimientoStockRow {
    nroInterno: number;
    idformulario: number;
    codArticulo: string;
    descripcion: string;
    /** UM base del artículo (si el API la expone). */
    unidadMedida?: string | null;
    tipoMov: string;
    descripcionMov?: string;
    cantidad: number;
    fechaMov: string;
    deposito: number;
    nroSuc: number;
    precioCosto: number;
    letra: string;
    nroComprobante: number;
}

function pickUnidadMedidaMovimiento(row: Record<string, unknown>): string {
    const nested = row.unidadMedida ?? row.UnidadMedida;
    if (nested && typeof nested === 'object') {
        const o = nested as Record<string, unknown>;
        return String(o.idUnidadMedida ?? o.IdUnidadMedida ?? o.descripcion ?? o.Descripcion ?? '').trim();
    }
    return String(
        nested ??
            row.idUnidadMedida ??
            row.IdUnidadMedida ??
            row.um ??
            row.Um ??
            '',
    ).trim();
}

export function normalizeMovimientoStockRow(raw: unknown): MovimientoStockRow {
    const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    return {
        nroInterno: Number(r.nroInterno ?? r.NroInterno ?? 0) || 0,
        idformulario: Number(r.idformulario ?? r.Idformulario ?? r.idFormulario ?? r.IdFormulario ?? 0) || 0,
        codArticulo: String(r.codArticulo ?? r.CodArticulo ?? r.codigoArticulo ?? r.CodigoArticulo ?? ''),
        descripcion: String(r.descripcion ?? r.Descripcion ?? ''),
        unidadMedida: pickUnidadMedidaMovimiento(r) || null,
        tipoMov: String(r.tipoMov ?? r.TipoMov ?? ''),
        descripcionMov: (r.descripcionMov ?? r.DescripcionMov) != null
            ? String(r.descripcionMov ?? r.DescripcionMov)
            : undefined,
        cantidad: Number(r.cantidad ?? r.Cantidad ?? 0) || 0,
        fechaMov: String(r.fechaMov ?? r.FechaMov ?? ''),
        deposito: Number(r.deposito ?? r.Deposito ?? 0) || 0,
        nroSuc: Number(r.nroSuc ?? r.NroSuc ?? 0) || 0,
        precioCosto: Number(r.precioCosto ?? r.PrecioCosto ?? 0) || 0,
        letra: String(r.letra ?? r.Letra ?? ''),
        nroComprobante: Number(r.nroComprobante ?? r.NroComprobante ?? 0) || 0,
    };
}

export interface MovimientosStockParams {
    fechaDesde?: Date | string;
    fechaHasta?: Date | string;
    codigoArticulo?: string;
    tipoMovimiento?: string;
    deposito?: number;
    page?: number;
    pageSize?: number;
}

export interface MovimientosStockResponse {
    data: MovimientoStockRow[];
    totalCount: number;
    totalCantidad: number;
    currentPage?: number;
    pageSize?: number;
    totalPages?: number;
}

export function formatMovimientoStockNumero(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('es-AR', { maximumFractionDigits: 4 });
}

/** Cantidad del movimiento con UM del artículo (ej. "14 UN"). */
export function formatMovimientoStockCantidad(
    cantidad: number,
    unidadMedida?: string | null,
): string {
    const numero = formatMovimientoStockNumero(cantidad);
    const um = String(unidadMedida ?? '').trim();
    return um ? `${numero} ${um}` : numero;
}

function buildMovimientosStockQuery(params: MovimientosStockParams): string {
    const queryParams = new URLSearchParams();
    if (params.fechaDesde) queryParams.append('fechaDesde', toQueryDateYMD(params.fechaDesde));
    if (params.fechaHasta) queryParams.append('fechaHasta', toQueryDateYMD(params.fechaHasta));
    if (params.codigoArticulo) queryParams.append('codigoArticulo', params.codigoArticulo);
    if (params.tipoMovimiento) queryParams.append('tipoMovimiento', params.tipoMovimiento);
    if (params.deposito) queryParams.append('deposito', params.deposito.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    return queryParams.toString();
}

const inventarioInformes = {
    getStockPorDeposito: async (params: StockDepositoParams): Promise<StockDepositoResponse> => {
        try {
            const response = await api.get(`/api/InventarioInformes/stock-deposito?${buildStockDepositoQuery(params)}`);
            return response.data;
        } catch (error) {
            console.error('Error en getStockPorDeposito:', error);
            throw error;
        }
    },

    getStockPorDepositoPdf: async (filters: {
        codigoArticulo?: string;
        descripcion?: string;
        nroUbicacion?: number;
        codigoLinea?: string;
    }) => {
        const params = new URLSearchParams();
        if (filters.codigoArticulo) params.append('codigoArticulo', filters.codigoArticulo);
        if (filters.descripcion) params.append('descripcion', filters.descripcion);
        if (filters.nroUbicacion) params.append('nroUbicacion', filters.nroUbicacion.toString());
        if (filters.codigoLinea) params.append('codigoLinea', filters.codigoLinea);

        const response = await api.get(`/api/InventarioInformes/stock-deposito/pdf?${params.toString()}`, {
            responseType: 'blob'
        });
        return response.data;
    },

    getUbicaciones: async () => {
        try {
            const response = await api.get('/api/InventarioInformes/ubicaciones');
            return response.data;
        } catch (error) {
            console.error('Error al obtener ubicaciones:', error);
            throw error;
        }
    },

    getMovimientosStock: async (params: MovimientosStockParams): Promise<MovimientosStockResponse> => {
        try {
            const response = await api.get(`/api/InventarioInformes/movimientos-stock?${buildMovimientosStockQuery(params)}`);
            const body = response.data ?? {};
            const rawRows = Array.isArray(body.data) ? body.data : [];
            return {
                ...body,
                data: rawRows.map(normalizeMovimientoStockRow),
                totalCount: Number(body.totalCount ?? 0) || 0,
                totalCantidad: Number(body.totalCantidad ?? 0) || 0,
            };
        } catch (error) {
            console.error('Error en getMovimientosStock:', error);
            throw error;
        }
    },

    getMovimientosStockPdf: async (filters: Omit<MovimientosStockParams, 'page' | 'pageSize'>) => {
        const params = new URLSearchParams();
        if (filters.fechaDesde) params.append('fechaDesde', toQueryDateYMD(filters.fechaDesde));
        if (filters.fechaHasta) params.append('fechaHasta', toQueryDateYMD(filters.fechaHasta));
        if (filters.codigoArticulo) params.append('codigoArticulo', filters.codigoArticulo);
        if (filters.tipoMovimiento) params.append('tipoMovimiento', filters.tipoMovimiento);
        if (filters.deposito) params.append('deposito', filters.deposito.toString());

        const response = await api.get(`/api/InventarioInformes/movimientos-stock/pdf?${params.toString()}`, {
            responseType: 'blob'
        });
        return response.data;
    },

    getStockAFecha: async (params: StockAFechaParams): Promise<StockAFechaResponse> => {
        const response = await api.get(`/api/InventarioInformes/stock-a-fecha?${buildStockAFechaQuery(params)}`);
        return response.data;
    },

    getStockAFechaPdf: async (filters: StockAFechaParams): Promise<Blob> => {
        const query = buildStockAFechaQuery({
            fechaHasta: filters.fechaHasta,
            codigoArticulo: filters.codigoArticulo,
            nroUbicacion: filters.nroUbicacion,
        });
        const response = await api.get(`/api/InventarioInformes/stock-a-fecha/pdf?${query}`, {
            responseType: 'blob',
        });
        return response.data;
    },

    getStockInmovilizado: async (params: StockInmovilizadoParams): Promise<StockInmovilizadoResponse> => {
        const response = await api.get(
            `/api/InventarioInformes/stock-inmovilizado?${buildStockInmovilizadoQuery(params)}`,
        );
        return response.data;
    },

    getStockInmovilizadoPdf: async (filters: StockInmovilizadoParams): Promise<Blob> => {
        const query = buildStockInmovilizadoQuery({
            fechaDesde: filters.fechaDesde,
            fechaHasta: filters.fechaHasta,
            codigoArticulo: filters.codigoArticulo,
            nroUbicacion: filters.nroUbicacion,
        });
        const response = await api.get(`/api/InventarioInformes/stock-inmovilizado/pdf?${query}`, {
            responseType: 'blob',
        });
        return response.data;
    },
};

export default inventarioInformes;
